// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState, type ReactNode, useMemo } from 'react';

import {
  type TaskTypeGroup,
  type TaskTypeGroupId,
  useEvolveDelete,
  useEvolvePatch,
  useEvolvePost,
  useEvolvePaginatedGet,
} from '@evolvemep/api-ts';
import { ActionIcon, Button, Flex, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { GridMenu } from 'components/Mantine/GridMenu';
import { InlineEditor } from 'components/Mantine/InlineEditor';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { TriggerOnVisible } from 'hooks/useOnScreen';

const TaskRowContainer = ({ children }: { children: ReactNode }) => (
  <Flex
    style={{ border: 'solid 1px lightgray', borderRadius: 5 }}
    flex={1}
    className="hover-title"
    p="sm"
    pl="md"
    gap="sm"
    h={50}
  >
    {children}
  </Flex>
);

const CreateNewGroup = ({ onClose, refresh }: { onClose: () => void; refresh: () => void }) => {
  const { apiCall: createTaskTypeGroup, loading: creating } = useEvolvePost('shop/taskTypeGroup');
  return (
    <TaskRowContainer>
      <InlineEditor
        defaultValue=""
        onSubmit={(name) => {
          void createTaskTypeGroup({ name }).then(() => {
            onClose();
            refresh();
          });
        }}
        onClose={onClose}
        loading={creating}
        buttonSide="right"
        flex={1}
        flexProps={{ flex: 1 }}
        placeholder="Type a new task group name"
      />
    </TaskRowContainer>
  );
};

const GroupRow = ({
  editing,
  setEditing,
  disabled,
  taskTypeGroup,
  reset,
}: {
  editing: boolean;
  setEditing: (newValue: boolean) => void;
  disabled: boolean;
  taskTypeGroup: TaskTypeGroup;
  reset: () => void;
}) => {
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const { apiCall: updateTaskTypeGroup, loading: updating } = useEvolvePatch(
    `shop/taskTypeGroup/${taskTypeGroup.taskTypeGroupId}`,
  );

  const { apiCall: deleteTaskTypeGroup, loading: deleting } = useEvolveDelete(
    `shop/taskTypeGroup/${taskTypeGroup.taskTypeGroupId}`,
  );

  const onConfirmDelete = () => {
    void deleteTaskTypeGroup().then(() => {
      notifications.show({
        title: 'Successfully deleted',
        message: `Group ${taskTypeGroup.name} successfully deleted`,
      });
      setDeleteConfirm(false);
      reset();
    });
  };

  const onSaveChanges = (name: string) => {
    void updateTaskTypeGroup({ name }).then(() => {
      notifications.show({
        title: 'Changes saved',
        message: `Changes saved to group ${taskTypeGroup.name}`,
      });
      setEditing(false);
      reset();
    });
  };

  return (
    <TaskRowContainer key={taskTypeGroup.taskTypeGroupId}>
      {editing ? (
        <InlineEditor
          defaultValue={taskTypeGroup.name}
          onSubmit={onSaveChanges}
          onClose={() => setEditing(false)}
          validate={(name) => name !== taskTypeGroup.name && name.length > 0}
          loading={updating}
          buttonSide="right"
          flex={1}
          flexProps={{ flex: 1 }}
          placeholder={`Enter a new name for ${taskTypeGroup.name}`}
        />
      ) : (
        <Flex justify="space-between" flex={1} align="center">
          <Text>{taskTypeGroup.name}</Text>
          <GridMenu
            disabled={disabled}
            options={['Edit', 'Delete']}
            onMenuItemSelected={(menuItem) => {
              if (menuItem === 'Edit') {
                setEditing(true);
              } else {
                setDeleteConfirm(true);
              }
            }}
          />
          <WrappedModal
            title="Delete group"
            opened={deleteConfirm}
            loading={deleting}
            onConfirm={onConfirmDelete}
            onClose={() => setDeleteConfirm(false)}
            confirmationText="Delete"
          >
            <Text>
              Once deleted, all tasks within the group will move into Ungrouped Tasks. Are you sure you want to delete
              the task group <b>{taskTypeGroup.name}</b>?
            </Text>
          </WrappedModal>
        </Flex>
      )}
    </TaskRowContainer>
  );
};

const ManageTaskGroupsModalContents = () => {
  const [taskToEdit, setTaskToEdit] = useState<TaskTypeGroupId | 'new-task' | null>(null);
  const [searchText, setSearchText] = useState('');

  const defaultOpts = useMemo(
    () => ({
      defaultConfig: {
        params: {
          name: searchText,
        },
      },
    }),
    [searchText],
  );
  const {
    data: groups,
    loading: loadingGroups,
    fetchNextPage: fetchNextPageOfGroups,
    setDefaultOpts,
    reset,
  } = useEvolvePaginatedGet('shop/taskTypeGroup', defaultOpts);
  useEffect(() => setDefaultOpts(defaultOpts), [defaultOpts, setDefaultOpts]);

  return (
    <>
      <Flex gap="md">
        <TextInputDebounced flex={1} onChange={setSearchText} value={searchText} placeholder="Search..." />
        <Button
          variant="light"
          leftSection={<EvolveIcon icon="Add" size="xs" />}
          onClick={() => setTaskToEdit('new-task')}
          disabled={taskToEdit !== null}
        >
          New task group
        </Button>
      </Flex>
      {taskToEdit === 'new-task' && <CreateNewGroup onClose={() => setTaskToEdit(null)} refresh={reset} />}
      <Stack style={{ overflowY: 'auto' }}>
        {groups.map((g) => (
          <GroupRow
            taskTypeGroup={g}
            key={g.taskTypeGroupId}
            reset={reset}
            editing={taskToEdit === g.taskTypeGroupId}
            setEditing={(isEditing) => setTaskToEdit(isEditing ? g.taskTypeGroupId : null)}
            disabled={taskToEdit !== null}
          />
        ))}
        {!loadingGroups && groups.length === 0 && taskToEdit !== 'new-task' && (
          <Text c="dimmed" fz="sm">
            {searchText ? 'No task groups matched your search.' : 'No task groups have been created yet.'}
          </Text>
        )}
        <TriggerOnVisible onVisible={fetchNextPageOfGroups} loading={loadingGroups} />
      </Stack>
    </>
  );
};

export const ManageTaskGroupsModal = ({ onRefreshTaskGroups }: { onRefreshTaskGroups: () => void }) => {
  const [opened, setOpened] = useState(false);

  return (
    <>
      <AgGridStyleTooltip label="Manage task groups" withArrow>
        <ActionIcon size="input-sm" variant="light" onClick={() => setOpened(true)}>
          <EvolveIcon icon="ManageTaskGroups" />
        </ActionIcon>
      </AgGridStyleTooltip>
      <WrappedModal
        title="Manage task groups"
        opened={opened}
        onClose={() => {
          onRefreshTaskGroups();
          setOpened(false);
        }}
        hideModalButtons
      >
        <ManageTaskGroupsModalContents />
      </WrappedModal>
    </>
  );
};
